// src/services/notificationsService.js
import { db } from '../lib/firebase';
import { errorDev } from '../utils/logger.js';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  doc,
  getDocs,
  limit
} from '../lib/firestoreClient';

const NOTIFICATIONS = 'notifications';

export const isNotificationSubscribed = (notification) => {
  if (!notification) return false;
  return notification.isSubscribed !== false && notification.unsubscribed !== true;
};

export const isNotificationUnread = (notification) => {
  if (!isNotificationSubscribed(notification)) return false;
  return !notification.isRead && !notification.read;
};

/**
 * Create a new notification for a user
 * @param {Object} options - Notification options
 * @param {string} options.toUserId - User ID to notify (required)
 * @param {string} [options.fromUserId] - Sender user ID
 * @param {string} [options.type='system'] - Notification type
 * @param {string} [options.content=''] - Notification content/message
 * @param {Object} [options.payload={}] - Additional payload data
 * @param {string} [options.title] - Notification title
 * @param {string} [options.body] - Notification body for push
 * @returns {Promise<{id: string}>} - Created notification reference
 */
export const createNotification = async ({ 
  toUserId, 
  fromUserId = null, 
  type = 'system', 
  content = '', 
  payload = {}, 
  title = null, 
  body = null 
}) => {
  if (!toUserId) {
    throw new Error('Recipient user ID is required');
  }
  
  const now = serverTimestamp();
  const docRef = await addDoc(collection(db, NOTIFICATIONS), {
    userId: toUserId,
    toUserId: toUserId, // Legacy compatibility
    fromUserId,
    type,
    content,
    title: title || null,
    body: body || null,
    payload: payload || {},
    isRead: false,
    isSubscribed: true,
    createdAt: now,
    sent: false
  });
  
  return { id: docRef.id };
};

/**
 * Listen to notifications for a user in real-time
 * Handles both modern (userId) and legacy (toUserId) fields
 * @param {string} uid - User ID to listen for
 * @param {Function} cb - Callback function receiving notification array
 * @returns {Function} - Unsubscribe function
 */
export const listenNotificationsForUser = (uid, cb) => {
  if (!uid) return () => {};

  const perQuery = new Map([
    ['userId', new Map()],
    ['toUserId', new Map()],
  ]);
  const listeners = [];

  const emit = () => {
    const merged = new Map();
    for (const [id, docData] of perQuery.get('toUserId').entries()) merged.set(id, docData);
    for (const [id, docData] of perQuery.get('userId').entries()) merged.set(id, docData);
    
    const arr = Array.from(merged.values()).sort((a, b) => {
      const aTs = a.createdAt?.seconds || (new Date(a.createdAt || 0)).getTime() / 1000;
      const bTs = b.createdAt?.seconds || (new Date(b.createdAt || 0)).getTime() / 1000;
      return (bTs || 0) - (aTs || 0);
    });
    
    const visible = arr.filter(isNotificationSubscribed);
    try { cb(visible); } catch (err) { errorDev("Operation failed:", err);}
  };

  const fallbackAttached = new Set();
  const attach = (q, key, fallbackQuery) => {
    try {
      const unsub = onSnapshot(
        q, 
        (snap) => {
          const fresh = new Map();
          snap.docs.forEach((d) => {
            fresh.set(d.id, { id: d.id, ...d.data() });
          });
          perQuery.set(key, fresh);
          emit();
        }, 
        (err) => {
          if (fallbackQuery && !fallbackAttached.has(key)) {
            fallbackAttached.add(key);
            attach(fallbackQuery, key);
            return;
          }
          errorDev('Notification listener error:', err);
        }
      );
      listeners.push(unsub);
    } catch (err) {
      // Silent fail for query attachment
    errorDev("Operation failed:", err);
    }
  };

  // Modern field query
  try {
    const q1 = query(
      collection(db, NOTIFICATIONS), 
      where('userId', '==', uid), 
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    const q1Fallback = query(
      collection(db, NOTIFICATIONS),
      where('userId', '==', uid),
      limit(100)
    );
    attach(q1, 'userId', q1Fallback);
  } catch (err) {
    // Silent fail
  errorDev("Operation failed:", err);
  }

  // Legacy field query
  try {
    const q2 = query(
      collection(db, NOTIFICATIONS), 
      where('toUserId', '==', uid), 
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    const q2Fallback = query(
      collection(db, NOTIFICATIONS),
      where('toUserId', '==', uid),
      limit(100)
    );
    attach(q2, 'toUserId', q2Fallback);
  } catch (err) {
    // Silent fail
  errorDev("Operation failed:", err);
  }

  return () => {
    listeners.forEach(unsub => {
      try { unsub(); } catch (err) { errorDev("Operation failed:", err);  /* ignore cleanup errors */ }
    });
  };
};

/**
 * Mark a single notification as read
 * @param {string} id - Notification document ID
 */
export const markNotificationRead = async (id) => {
  if (!id) return;
  try {
    await updateDoc(doc(db, NOTIFICATIONS, id), { isRead: true, read: true });
  } catch (err) {
    // Silent fail - non-critical operation
  errorDev("Operation failed:", err);
  }
};

export const dismissNotification = async (id) => {
  if (!id) return;
  try {
    await updateDoc(doc(db, NOTIFICATIONS, id), {
      isSubscribed: false,
      unsubscribed: true,
      unsubscribedAt: serverTimestamp(),
    });
  } catch (err) {
    // Silent fail - non-critical operation
  errorDev("Operation failed:", err);
  }
};

/**
 * Create a generic notification
 */
export const notifyGeneric = async ({ 
  toUserId, 
  fromUserId = null, 
  type = 'system', 
  content = '', 
  payload = {} 
}) => {
  return createNotification({ toUserId, fromUserId, type, content, payload });
};

/**
 * Notify user about earned points
 */
export const notifyPointsEarned = async ({
  userId,
  points,
  reason
}) => {
  const message = `You earned ${points} points for ${reason}`;
  return createNotification({
    toUserId: userId,
    type: 'POINTS_EARNED',
    content: message,
    payload: { points, reason, contextType: 'points_earned' },
    title: 'Points Earned',
    body: message
  });
};

/**
 * Generic user notification helper
 */
export const notifyUser = async (opts) => createNotification(opts);

/**
 * Notify user about offer status updates
 */
export const notifyOfferUpdate = async ({ 
  toUserId, 
  fromUserId, 
  productId, 
  productName, 
  offerAmount, 
  status 
}) => {
  const isAccepted = status === 'accepted';
  const message = isAccepted
    ? `Your offer of ${offerAmount} for ${productName} was accepted`
    : `Your offer of ${offerAmount} for ${productName} was ${status}`;

  return createNotification({
    toUserId,
    fromUserId,
    type: 'OFFER_UPDATE',
    content: message,
    payload: { productId, productName, offerAmount, status, contextType: 'marketplace_offer' },
    title: 'Offer Update',
    body: message
  });
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<{success: boolean, count: number}>}
 */
const readRecipientStreams = async (userId) => {
  const targets = [
    { field: 'userId', label: 'modern' },
    { field: 'toUserId', label: 'legacy' },
  ];

  const results = await Promise.allSettled(
    targets.map(({ field }) =>
      getDocs(query(collection(db, NOTIFICATIONS), where(field, '==', userId), where('isRead', '==', false)))
    )
  );

  const streams = [];
  const failures = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      streams.push(result.value.docs || []);
    } else {
      failures.push({ field: targets[index].field, label: targets[index].label, error: result.reason });
    }
  });

  // A permission, index, or quota failure is not an empty inbox. When every
  // compatibility stream fails we must not report success with zero work done.
  if (streams.length === 0 && failures.length > 0) {
    const error = new Error('Failed to read notifications');
    error.code = 'notifications/read-failed';
    error.failures = failures;
    throw error;
  }

  failures.forEach(({ field, error }) => {
    errorDev(`Notification read failed for field "${field}":`, error);
  });

  return streams.flat();
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<{success: boolean, count: number}>}
 */
export const markAllNotificationsRead = async (userId) => {
  if (!userId) throw new Error('User ID is required');

  const docs = await readRecipientStreams(userId);

  const uniqueDocs = new Map();
  docs.forEach((d) => {
    uniqueDocs.set(d.id, d);
  });

  const results = await Promise.allSettled(
    Array.from(uniqueDocs.values())
      .filter((d) => isNotificationSubscribed(d.data()))
      .map((d) => updateDoc(d.ref, { isRead: true, read: true }))
  );

  const written = results.filter((result) => result.status === 'fulfilled').length;

  if (uniqueDocs.size > 0 && written === 0) {
    const error = new Error('Failed to mark notifications as read');
    error.code = 'notifications/update-failed';
    throw error;
  }

  return { success: true, count: written };
};

export const dismissAllNotifications = async (userId) => {
  if (!userId) throw new Error('User ID is required');

  const docs = await readRecipientStreams(userId);

  const uniqueDocs = new Map();
  docs.forEach((d) => {
    uniqueDocs.set(d.id, d);
  });

  const results = await Promise.allSettled(
    Array.from(uniqueDocs.values())
      .filter((d) => isNotificationSubscribed(d.data()))
      .map((d) => updateDoc(d.ref, {
        isSubscribed: false,
        unsubscribed: true,
        unsubscribedAt: serverTimestamp(),
      }))
  );

  const written = results.filter((result) => result.status === 'fulfilled').length;

  if (uniqueDocs.size > 0 && written === 0) {
    const error = new Error('Failed to dismiss notifications');
    error.code = 'notifications/update-failed';
    throw error;
  }

  return { success: true, count: written };
};

/**
 * Get unread notification count for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>}
 */
export const getUnreadCount = async (userId) => {
  if (!userId) return 0;

  try {
    const docs = await readRecipientStreams(userId);

    const unique = new Map();
    docs.forEach((d) => {
      unique.set(d.id, d.data());
    });

    return Array.from(unique.values()).filter((docData) => isNotificationUnread(docData)).length;
  } catch (err) {
    errorDev("Unread notification count failed:", err);
    return 0;
  }
};
